import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import {
  SellAllPositionsButton,
  SellPositionButton,
} from '@root/features/trade/position/sell-position'
import { StringUtil } from '@root/shared'

import { PositionsTableAmountCell } from './positions-table-amount-cell'
import { PositionsTableCurrencyCell } from './positions-table-currency-cell'
import { PositionsTablePnlCell } from './positions-table-pnl-cell'
import { PositionTableSellHeader } from './positions-table-sell-header'
import { PositionsTableVisibilityCell } from './positions-table-visibility-cell'
import { PositionsTableWalletCell } from './positions-table-wallet-cell'

import type { PositionTableColumnId } from '../atoms'
import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { Column } from '@root/shared/ui-kit'

const BoughtCell = ({
  value,
}: {
  value: WalletPositionModel | AggregatedPositionModel
}) => {
  const [boughtSizeUsd] = useAtom(value.boughtSizeUsd)
  const [boughtSizeNative] = useAtom(value.boughtSizeNative)
  const [boughtSizeCurrency] = useAtom(value.boughtSizeCurrency)

  return (
    <PositionsTableAmountCell
      amountUsd={boughtSizeUsd}
      amountNative={boughtSizeNative}
      amountCurrency={boughtSizeCurrency}
      type="bought"
    />
  )
}

const SoldCell = ({
  value,
}: {
  value: WalletPositionModel | AggregatedPositionModel
}) => {
  const [soldSizeUsd] = useAtom(value.soldSizeUsd)
  const [soldSizeNative] = useAtom(value.soldSizeNative)
  const [soldSizeCurrency] = useAtom(value.soldSizeCurrency)

  return (
    <PositionsTableAmountCell
      amountUsd={soldSizeUsd}
      amountNative={soldSizeNative}
      amountCurrency={soldSizeCurrency}
      type="sold"
    />
  )
}

const SellButtonsCell = ({
  value,
  withSellAll = false,
}: {
  value: AggregatedPositionModel | WalletPositionModel
  withSellAll?: boolean
}) => {
  if (!withSellAll) {
    return <SellPositionButton position={value} />
  }

  return (
    <SellButtons>
      <SellPositionButton position={value} />
      <SellAllPositionsButton position={value} />
    </SellButtons>
  )
}

const SellButtons = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
`

const RemainingCell = ({
  value,
}: {
  value: WalletPositionModel | AggregatedPositionModel
}) => {
  const [positionSizeUsd] = useAtom(value.remainingSizeUsd)
  const [positionSizeNative] = useAtom(value.remainingSizeNative)
  const [positionSizeCurrency] = useAtom(value.remainingSizeCurrency)

  return (
    <PositionsTableAmountCell
      amountUsd={positionSizeUsd}
      amountNative={positionSizeNative}
      amountCurrency={positionSizeCurrency}
      type="remaining"
    />
  )
}

export const createPositionsTableColumns = ({
  withSellAll,
}: {
  withSellAll?: boolean
}) => {
  const positionsTableColumns: Array<
    Column<
      WalletPositionModel | AggregatedPositionModel,
      PositionTableColumnId,
      {
        onOpenWalletPositions?: (value: AggregatedPositionModel) => void
      }
    >
  > = [
    {
      id: 'currency',
      header: ({ t }) => t('token'),
      render: ({ value }) => <PositionsTableCurrencyCell pair={value.pair} />,
      minWidth: 120,
    },

    {
      id: 'bought',
      header: ({ t }) => t('position.bought'),
      render: BoughtCell,
      minWidth: 90,
      sortable: true,
    },
    {
      id: 'sold',
      header: ({ t }) => t('position.sold'),
      render: SoldCell,
      minWidth: 90,
      sortable: true,
    },
    {
      id: 'remaining',
      header: ({ t }) => t('position.remaining'),
      render: RemainingCell,
      minWidth: 90,
      sortable: true,
    },
    {
      id: 'wallet',
      header: ({ t }) => StringUtil.capitalize(t('wallet_one')),
      render: ({ value, context }) => (
        <PositionsTableWalletCell
          position={value}
          onWalletsClick={(wallet, e) => {
            e.stopPropagation()
            context.onOpenWalletPositions?.(wallet)
          }}
        />
      ),
      minWidth: 100,
    },
    {
      id: 'pnl',
      header: ({ t }) => t('common.pnl'),
      render: ({ value }) => <PositionsTablePnlCell position={value} />,
      minWidth: 90,
      sortable: true,
    },
    {
      id: 'visibility',
      header: ({ t }) => t('position.table.visibility'),
      render: ({ value }) => <PositionsTableVisibilityCell position={value} />,
      width: 50,
    },

    {
      id: 'sell',
      header: () => <PositionTableSellHeader />,
      render: (props) => (
        <SellButtonsCell value={props.value} withSellAll={withSellAll} />
      ),
      minWidth: 220,
      maxWidth: 240,
      justify: 'end',
    },
  ]

  return positionsTableColumns
}
